import { environment } from '~/constants/environment';

import { type SelectedUserData } from '~/data/user/useQueryUserData';

import { toTitleCase, unique } from '@vestigas/utils';
import { activeApiName } from '~/utils/environment';

import { GENERIC_EMAIL_LOCALS, SYSTEM_ACCOUNT_IDS } from './constants';

type BugReportContext = {
  readonly apiEnvironment: string;
  readonly appEnvironment: {
    readonly hostname: string;
    readonly tier: string;
  };
  readonly company: {
    readonly id: string;
    readonly name: string;
  };
  readonly companyAccount: {
    readonly id: string;
    readonly name: string;
  };
  readonly featureFlags: Record<string, boolean>;
  readonly permissions: string[];
  readonly route: string;
  readonly userAgent: string;
  readonly viewport: {
    readonly devicePixelRatio: number;
    readonly height: number;
    readonly width: number;
  };
};

/**
 * Collects a rich context snapshot that is attached to every bug report.
 *
 * The values are evaluated lazily (inside the returned collector) so the
 * snapshot reflects the state at submit time, not at dialog open time.
 *
 * The cyclomatic complexity comes from defensive `??` fallbacks on optional
 * user-data fields and `globalThis.*` reads — there is no branching logic to
 * extract or simplify.
 */
// fallow-ignore-next-line complexity
export function getBugReportContext(
  currentUser: SelectedUserData | undefined,
): BugReportContext {
  return {
    // e.g. "DEV3 API" — derived from VITE_API_URL; distinguishes the API
    // backend the app is talking to (can differ from the app host when the
    // dev environment switcher is used).
    apiEnvironment: activeApiName,
    appEnvironment: {
      hostname: globalThis.location.hostname,
      // e.g. "development" | "beta" | "staging" | "production"
      tier: environment,
    },
    company: {
      id: String(currentUser?.companyInfo?.id ?? ''),
      name: String(currentUser?.companyInfo?.name ?? ''),
    },
    companyAccount: {
      id: String(currentUser?.companyAccountInfo?.id ?? ''),
      name: String(currentUser?.companyAccountInfo?.name ?? ''),
    },
    // Only keep enabled flags to keep the payload small and focused on
    // what the user currently has access to.
    featureFlags: Object.fromEntries(
      Object.entries(currentUser?.featureFlags ?? {}).filter(
        ([, enabled]) => enabled,
      ),
    ),
    permissions: (currentUser?.userPermissions ?? []).map(String),
    route: `${globalThis.location.pathname}${globalThis.location.search}`,
    userAgent: globalThis.navigator?.userAgent ?? '',
    viewport: {
      devicePixelRatio: globalThis.devicePixelRatio ?? 1,
      height: globalThis.innerHeight ?? 0,
      width: globalThis.innerWidth ?? 0,
    },
  };
}

type GetReporterNameInput = {
  readonly email?: string;
};

/**
 * Derives a display name for a bug-report reporter from their email address.
 * Parses the local-part (e.g. philip.baumann@… → "Philip Baumann").
 * Returns null when no reliable name can be determined — the user must enter one manually.
 */
export function getReporterName({
  email,
}: GetReporterNameInput): string | null {
  const localPart = email?.split('@')[0];
  if (!localPart) {
    return null;
  }

  // Reject generic / system account local parts
  if (GENERIC_EMAIL_LOCALS.has(localPart.toLowerCase())) {
    return null;
  }

  // Split on common separator characters
  const parts = localPart.split(/[.-]+/).filter(Boolean);

  // Take first and last part
  const firstName = parts.at(0);
  const lastName = parts.at(-1);
  const nameParts = unique(
    [firstName, lastName].filter((p): p is string => Boolean(p)),
  );

  // Reject if any segment is a generic keyword
  if (nameParts.some((p) => GENERIC_EMAIL_LOCALS.has(p.toLowerCase())))
    return null;

  return toTitleCase(nameParts.join(' '));
}

/**
 * Returns true for accounts that represent a system/kiosk user rather than a
 * real person. Used to decide whether the reporter name field requires manual
 * input in the bug report dialog.
 */
export function isSystemAccount({
  email,
  id,
}: {
  readonly email?: string;
  readonly id?: string;
}): boolean {
  if (id && SYSTEM_ACCOUNT_IDS.has(id)) {
    return true;
  }

  if (email) {
    const localPart = email.split('@')[0]?.toLowerCase() ?? '';
    if (GENERIC_EMAIL_LOCALS.has(localPart)) {
      return true;
    }
  }

  return false;
}
